import { CommandType } from '~/protos/agents'

import { BillingInfo } from '~/types/ResponseType'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'

export const getSuperAdminHeartbeat = async () => {
  const path = '/api/super-admin/heartbeat'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get super admin heartbeat. ${reason}`)
  }
}

export const getSignupsCSV = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/signups?${queryParams}`
    : '/api/super-admin/csv/signups'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download signups CSV. ${reason}`)
  }

  return response.blob()
}

export const getUserActivitiesCSV = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/user-activities?${queryParams}`
    : '/api/super-admin/csv/user-activities'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download user activities CSV. ${reason}`)
  }

  return response.blob()
}

export const getLastActiveUsersCSV = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/last-active-users?${queryParams}`
    : '/api/super-admin/csv/last-active-users'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download last active users CSV. ${reason}`)
  }

  return response.blob()
}

export const getUserTaskSubmission = async (queryParams?: string): Promise<Blob> => {
  const url = queryParams
    ? `/api/super-admin/csv/user-task-submissions?${queryParams}`
    : '/api/super-admin/csv/user-task-submissions'

  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download user activities CSV. ${reason}`)
  }

  return response.blob()
}

export const getAccessLogsByEmailCSV = async (queryParams: string): Promise<Blob> => {
  const url = `/api/super-admin/lookup/email-access-logs?${queryParams}`
  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to download access logs by email CSV. ${reason}`)
  }
  return response.blob()
}

export const getUserByUuid = async (queryParams: string): Promise<Blob> => {
  const url = `/api/super-admin/lookup/uuid-user?${queryParams}`
  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to get email. ${reason}`)
  }
  return response.blob()
}

export const getUserIdByEmail = async (queryParams: string): Promise<Blob> => {
  const url = `/api/super-admin/lookup/email-userid?${queryParams}`
  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })
  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to get user ID by email. ${reason}`)
  }
  return response.blob()
}

export const getSettingsCampaignAgentType = async () => {
  const path = '/api/super-admin/settings/campaigns-agent-type'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get campaign agent type settings. ${reason}`)
  }
  return response.json()
}

export const updateSettingsCampaignAgentType = async (payload: { agentType: CommandType }) => {
  const { agentType } = payload
  const path = '/api/super-admin/settings/campaigns-agent-type'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ agentType })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update campaign agent type settings. ${reason}`)
  }
}

export const updateAccountType = async (BillingInfo: BillingInfo) => {
  const path = '/api/super-admin/billing/update-billing-type'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(BillingInfo)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update billing type. ${reason}`)
  }
}

// ============================================================================
// Purchased Credits Management (Super Admin)
// ============================================================================

export interface SuperAdminCreditBalance {
  billing_id: number
  total_credits: number
  subscription_remaining: number
  purchased_credits: number
  carried_over_credits: number
  credit_debt: number
  carryover_debt: number
  subscription_limit: number
  subscription_used: number
  period_start?: string
  period_end?: string
  purchase_history: Array<{
    credits: number
    purchased_at?: string
    pack_type: string
    refunded?: boolean
    amount_total_cents?: number
    currency?: string
    receipt_url?: string
    admin_email?: string
    reason?: string
  }>
}

export interface CreditOverridesRequest {
  purchased_credits?: number
  carried_over_credits?: number
  credit_debt?: number
  carryover_debt?: number
}

export interface CreditOverridesResponse {
  billing_id: number
  purchased_credits?: number
  carried_over_credits?: number
  credit_debt?: number
  carryover_debt?: number
  message: string
}

export interface AdjustCreditsResponse {
  billing_id: number
  previous_purchased_credits: number
  new_purchased_credits: number
  adjustment: number
  reason: string
}

export const getSuperAdminCreditBalance = async (
  billingId: number
): Promise<SuperAdminCreditBalance> => {
  const path = `/api/super-admin/billing/${billingId}/credits`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get credit balance. ${reason}`)
  }
  return response.json()
}

export const adjustSuperAdminCredits = async (
  billingId: number,
  amount: number,
  reason: string
): Promise<AdjustCreditsResponse> => {
  const path = `/api/super-admin/billing/${billingId}/credits/adjust`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount, reason })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to adjust credits. ${reason}`)
  }
  return response.json()
}

export const overrideCreditValues = async (
  billingId: number,
  overrides: CreditOverridesRequest
): Promise<CreditOverridesResponse> => {
  const path = `/api/super-admin/billing/${billingId}/credits/override`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(overrides)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to override credit values. ${reason}`)
  }
  return response.json()
}

// Lost/Expired License Credits Recovery

export interface LostCreditsInfo {
  license_id: number
  license_type_id: number | null
  start_date: string | null
  end_date: string | null
  purchased_credits: number
  purchase_history: Array<{
    credits: number
    purchased_at?: string
    pack_type: string
    admin_email?: string
    reason?: string
  }>
  is_active: boolean
  is_deleted: boolean
}

export interface RecoverCreditsResponse {
  billing_id: number
  recovered_credits: number
  recovered_from_licenses: number[]
  new_purchased_credits: number
  target_license_id: number
}

export const findLostPurchasedCredits = async (billingId: number): Promise<LostCreditsInfo[]> => {
  const path = `/api/super-admin/billing/${billingId}/credits/lost`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to find lost credits. ${reason}`)
  }
  return response.json()
}

export const recoverLostPurchasedCredits = async (
  billingId: number
): Promise<RecoverCreditsResponse> => {
  const path = `/api/super-admin/billing/${billingId}/credits/recover`
  const response = await fetchWrapper(path, { method: 'POST' })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to recover credits. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Billing Payments Overview (DB-driven, instant load)
// ============================================================================

export interface PaymentMember {
  user_id: number
  name: string | null
  email: string | null
  role: string | null
}

export interface PaymentLicense {
  license_id: number
  license_type: string | null
  license_label: string | null
  start_date: string | null
  end_date: string | null
  auto_renew: boolean
  subscription_id: string | null
}

export interface BillingPaymentRow {
  billing_id: number
  billing_name: string | null
  billing_email: string | null
  billing_to: string | null
  account_type: string | null
  created_time: string | null

  has_payment_method: boolean
  payment_method_brand: string | null
  payment_method_last4: string | null
  payment_method_exp: string | null

  stripe_customer_id: string | null
  subscription_id: string | null
  subscription_active: boolean
  subscription_status: string | null

  active_license: PaymentLicense | null

  current_month_usage: number
  credit_debt: number

  account_locked: boolean
  lock_reason: string | null
  note: string | null

  workspace_count: number
  workspace_names: string[]

  member_count: number
  members: PaymentMember[]

  deleted: boolean
}

export interface InvoicePaymentRow {
  invoice_id: string
  invoice_number: string | null
  amount_due: number
  amount_paid: number
  currency: string
  status: string | null
  created_at: string | null
  due_date: string | null
  paid_at: string | null
  description: string | null
  hosted_invoice_url: string | null
  pdf_url: string | null
  invoice_type: string

  billing_id: number
  billing_name: string | null
  billing_email: string | null
  billing_to: string | null
  account_type: string | null
  stripe_customer_id: string | null
}

export const getAllInvoices = async (): Promise<InvoicePaymentRow[]> => {
  const path = '/api/super-admin/all-invoices'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to fetch invoices. ${reason}`)
  }
  return response.json()
}

export const getTasksDomainList = async () => {
  const path = '/api/super-admin/tasks/domains'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks domain list. ${reason}`)
  }
  return response.json()
}

export interface BrowseWorkspace {
  id: number
  uuid: string
  name: string
  role: string
}

export interface BrowseAccount {
  id: number | null
  name: string
  workspaces: BrowseWorkspace[]
}

export interface BrowseEmail {
  email: string
  user_id: number
  name: string | null
  accounts: BrowseAccount[]
}

export interface BrowseDomain {
  domain: string
  emails: BrowseEmail[]
}

export interface BrowseDataResponse {
  data: BrowseDomain[]
}

export const getBrowseData = async (): Promise<BrowseDataResponse> => {
  const path = '/api/super-admin/tasks/browse-data'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get browse data. ${reason}`)
  }
  return response.json()
}

export const getTasksEmailList = async (domain: string) => {
  const path = `/api/super-admin/tasks/emails?domain=${encodeURIComponent(domain)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks email list. ${reason}`)
  }
  return response.json()
}

export const getFullEmailList = async () => {
  const path = `/api/super-admin/tasks/emails?domain=`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get full email list. ${reason}`)
  }
  return response.json()
}

export const getTaskbyId = async (taskId: string) => {
  const path = `/api/super-admin/tasks/taskid?taskid=${taskId}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get task by ID. ${reason}`)
  }
  return response.json()
}

export const getTaskNavigation = async (taskId: string | number) => {
  const path = `/api/super-admin/tasks/navigation?taskid=${taskId}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get task navigation. ${reason}`)
  }
  return response.json() as Promise<{
    task_id: number
    workspace_uuid: string | null
    conversation_uuid: string | null
    org_id: number | null
    conversation_id: number | null
    deleted?: boolean
    task_deleted?: boolean
    conversation_deleted?: boolean
    org_deleted?: boolean
  }>
}

export const getTasksWorkspaceList = async (email: string) => {
  const path = `/api/super-admin/tasks/workspaces?email=${encodeURIComponent(email)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks workspace list. ${reason}`)
  }
  return response.json()
}

export const getRecentInactiveUsers = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/recent-inactive-users?${queryParams}`
    : '/api/super-admin/csv/recent-inactive-users'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get recent inactive users. ${reason}`)
  }

  return response.blob()
}

export const getInactiveUsers = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/inactive-users?${queryParams}`
    : '/api/super-admin/csv/inactive-users'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get inactive users. ${reason}`)
  }

  return response.blob()
}

export const getTestDriveEmails = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/test-drive-emails?${queryParams}`
    : '/api/super-admin/csv/test-drive-emails'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get test drive emails. ${reason}`)
  }

  return response.blob()
}

export const getTestDriveUsersTaskSubmissionCount = async (queryParams?: string) => {
  const url = queryParams
    ? `/api/super-admin/csv/test-drive-emails-submission-count?${queryParams}`
    : '/api/super-admin/csv/test-drive-emails-submission-count'

  const response = await fetchWrapper(url)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get test drive users task submission count. ${reason}`)
  }

  return response.blob()
}

export const getTasksConversations = async (orgId: number) => {
  const path = `/api/super-admin/tasks/conversations?org_id=${orgId}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks conversations. ${reason}`)
  }
  return response.json()
}

export interface MessageBlock {
  type: string
  text?: {
    type: string
    text: string
  }
  fields?: Record<string, unknown>
}

export interface ConversationMessage {
  id: number
  role: 'user' | 'assistant'
  text: string | null
  blocks: MessageBlock[] | null
  message_metadata: Record<string, unknown> | null
  created_time: string | null
}

export const getConversationMessages = async (conversationId: number) => {
  const path = `/api/super-admin/tasks/conversation-messages?conversation_id=${conversationId}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get conversation messages. ${reason}`)
  }
  return response.json() as Promise<{ messages: ConversationMessage[] }>
}

export const getTasksTasks = async (conversationId: number) => {
  const path = `/api/super-admin/tasks/tasks?conversation_id=${conversationId}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks by conversation ID. ${reason}`)
  }
  return response.json()
}

export const getTasksByConversationUuid = async (uuid: string) => {
  const path = `/api/super-admin/tasks/tasks-by-conversation-uuid?uuid=${encodeURIComponent(uuid)}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get tasks by conversation UUID. ${reason}`)
  }
  return response.json() as Promise<{
    conversation_id: number
    conversation_uuid: string
    tasks: Array<{
      id: number
      name: string
      status: string
      created_at: string | null
      updated_at: string | null
    }>
  }>
}

export const getSuperAdminTaskDetails = async (taskId: number) => {
  const path = `/api/super-admin/tasks/taskdetails?taskid=${taskId}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get task details. ${reason}`)
  }
  return response.json()
}

export const getTaskAnalytics = async (
  erroredLimit: number = 15,
  topUsersLimit: number = 20,
  topUsersSinceDate?: string
) => {
  let path = `/api/super-admin/tasks/analytics?errored_limit=${erroredLimit}&top_users_limit=${topUsersLimit}`

  if (topUsersSinceDate) {
    path += `&top_users_since_date=${encodeURIComponent(topUsersSinceDate)}`
  }

  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get task analytics. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// User Activity Leaderboard
// ============================================================================

export interface LeaderboardUser {
  rank: number
  user_id: number
  email: string
  name: string
  credits_used: number
  task_count: number
  message_count: number
  conversation_count: number
  total_tokens: number
}

export interface LeaderboardSummary {
  total_users: number
  total_credits: number
  total_tasks: number
  total_messages: number
}

export interface UserActivityLeaderboardResponse {
  leaderboard: LeaderboardUser[]
  summary: LeaderboardSummary
  metadata: {
    start_date: string | null
    end_date: string | null
    start_date_utc: string | null
    end_date_utc: string | null
    timezone: string
    limit: number
  }
}

export const getUserActivityLeaderboard = async (params: {
  startDate?: string
  endDate?: string
  timezone?: string
  limit?: number
}): Promise<UserActivityLeaderboardResponse> => {
  const searchParams = new URLSearchParams()

  if (params.startDate) {
    searchParams.set('start_date', params.startDate)
  }
  if (params.endDate) {
    searchParams.set('end_date', params.endDate)
  }
  if (params.timezone) {
    searchParams.set('timezone', params.timezone)
  }
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString())
  }

  const queryString = searchParams.toString()
  const path = `/api/super-admin/tasks/user-activity-leaderboard${queryString ? `?${queryString}` : ''}`

  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user activity leaderboard. ${reason}`)
  }
  return response.json()
}

export const getAvailableCommandTypes = async () => {
  const path = '/api/super-admin/tasks/available-command-types'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get available command types. ${reason}`)
  }
  return response.json()
}

export const getRandomTaskByCommandType = async (cmdType: string) => {
  const path = `/api/super-admin/tasks/random-by-command-type?cmd_type=${encodeURIComponent(cmdType)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get random task. ${reason}`)
  }
  return response.json()
}

export interface UserTaskByEmail {
  task_id: number
  conversation_id: number | null
  cmd_type: string
  status: string
  created_at: string | null
  updated_at: string | null
}

export interface UserConversationByEmail {
  id: number
  uuid: string
  title: string
  mode: string | null
  created_at: string | null
  updated_at: string | null
  org_id: number
  workspace_uuid: string | null
  message_count: number
}

export interface UserDataByEmailResponse {
  tasks: UserTaskByEmail[]
  conversations: UserConversationByEmail[]
}

export const getUserTasksByEmail = async (
  email: string
): Promise<UserDataByEmailResponse & { user_id?: number }> => {
  const path = `/api/super-admin/tasks/user-tasks-by-email?email=${encodeURIComponent(email)}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user tasks by email. ${reason}`)
  }
  const data = await response.json()
  return {
    user_id: data.user_id,
    tasks: data.tasks || [],
    conversations: data.conversations || []
  }
}

// ============================================================================
// Super-admin: workspace / billing lookup + usage (Find tab drill-down)
// ============================================================================

export interface WorkspaceLookupResponse {
  org_id: number
  uuid: string
  name: string
  billing_id: number | null
  billing_name: string | null
  deleted: boolean
}

export const getWorkspaceLookup = async (q: string): Promise<WorkspaceLookupResponse> => {
  const path = `/api/super-admin/tasks/workspace-lookup?q=${encodeURIComponent(q.trim())}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to resolve workspace. ${reason}`)
  }
  return response.json()
}

export interface BillingLookupResponse {
  billing_id: number
  name: string | null
  billing_email: string | null
  workspaces: Array<{ org_id: number; uuid: string; name: string }>
}

export const getBillingLookup = async (billingId: number): Promise<BillingLookupResponse> => {
  const path = `/api/super-admin/tasks/billing-lookup?billing_id=${billingId}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to resolve billing account. ${reason}`)
  }
  return response.json()
}

export interface AdminUsageSummary {
  scope: string
  credits: number
  /** Sum of input + output. Wire payloads may omit token splits before rollout; `getAdminUsageSummary` coerces. */
  total_tokens: number
  input_tokens: number
  output_tokens: number
  billable_flows: number
}

/**
 * Fetches admin usage summary. Normalizes token fields for rollout: missing splits default to 0;
 * `total_tokens` uses the API value when present, otherwise `input_tokens + output_tokens` when either
 * split is present (legacy payloads may expose only an aggregate total).
 */
export const getAdminUsageSummary = async (params: {
  scope: 'billing' | 'workspace' | 'conversation' | 'task' | 'user'
  billingId?: number
  orgId?: number
  conversationId?: number
  taskId?: number
  userId?: number
}): Promise<AdminUsageSummary> => {
  const sp = new URLSearchParams()
  sp.set('scope', params.scope)
  if (params.billingId != null) sp.set('billing_id', String(params.billingId))
  if (params.orgId != null) sp.set('org_id', String(params.orgId))
  if (params.conversationId != null) sp.set('conversation_id', String(params.conversationId))
  if (params.taskId != null) sp.set('task_id', String(params.taskId))
  if (params.userId != null) sp.set('user_id', String(params.userId))
  const path = `/api/super-admin/tasks/admin-usage-summary?${sp.toString()}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get usage summary. ${reason}`)
  }
  const raw = (await response.json()) as Partial<AdminUsageSummary> &
    Pick<AdminUsageSummary, 'scope'>
  if (typeof raw.scope !== 'string' || raw.scope.length === 0) {
    throw new Error('Invalid admin usage summary response: missing or invalid scope')
  }
  const inputTokens = raw.input_tokens ?? 0
  const outputTokens = raw.output_tokens ?? 0
  // If the API sends only total_tokens (legacy), keep it; if only splits, sum; if both, prefer explicit total when present.
  const totalTokens =
    raw.total_tokens ??
    (raw.input_tokens != null || raw.output_tokens != null ? inputTokens + outputTokens : 0)
  return {
    scope: raw.scope,
    credits: raw.credits ?? 0,
    billable_flows: raw.billable_flows ?? 0,
    input_tokens: inputTokens,
    output_tokens: outputTokens,
    total_tokens: totalTokens
  }
}

export const getRawTaskObject = async (taskId: number) => {
  const path = `/api/super-admin/tasks/raw-task-object?taskid=${taskId}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get raw task object. ${reason}`)
  }
  return response.json()
}

export interface TaskCreditUsage {
  task_id: number
  task: {
    input_tokens: number
    output_tokens: number
    total_tokens: number
    credits: number
    event_count: number
    model_breakdown: Array<{
      model: string
      input_tokens: number
      output_tokens: number
      raw_credits: number
      calls: number
    }>
  }
  conversation: {
    conversation_id: number
    input_tokens: number
    output_tokens: number
    total_tokens: number
    credits: number
  } | null
}

export const getTaskCreditUsage = async (taskId: number): Promise<TaskCreditUsage> => {
  const path = `/api/super-admin/tasks/credit-usage?taskid=${taskId}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get task credit usage. ${reason}`)
  }
  return response.json()
}

export interface ConversationCreditUsage {
  conversation_id: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
  credits: number
  event_count: number
  model_breakdown: Array<{
    model: string
    input_tokens: number
    output_tokens: number
    raw_credits: number
    calls: number
  }>
}

export const getConversationCreditUsage = async (
  conversationId: number
): Promise<ConversationCreditUsage> => {
  const path = `/api/super-admin/tasks/conversation-credit-usage?conversation_id=${conversationId}`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get conversation credit usage. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Billing Account CRUD Operations
// ============================================================================

export interface BillingAccountData {
  id: number
  name: string
  billing_email: string
  billing_to: string | null
  note: string | null
  billing_metadata: Record<string, unknown>
  created_time: string | null
  updated_time: string | null
  has_payment_method: boolean
  stripe_customer_id: string | null
  has_personal_workspace: boolean
  member_count: number // Number of account admins/members
  can_delete: boolean // True if account has no members and can be deleted
  license_types: string[] // Active license type labels (e.g. ["Enterprise", "Pro+"])
  deleted?: boolean // True if billing account is soft-deleted
  deleted_at?: string | null // ISO timestamp of soft-delete, if any
}

export interface CreateBillingAccountRequest {
  name: string
  note?: string
}

export interface UpdateBillingAccountRequest {
  id: number
  name?: string
  note?: string
}

export interface AccountTypeOption {
  value: string
  label: string
}

export interface BillingMember {
  user_id?: number
  invitation_id?: number
  email: string
  name: string | null
  role: 'account_admin' | 'member'
  is_account_admin: boolean
  status: 'active' | 'pending'
  added_time: string | null
  expires_at?: string | null
}

export const getBillingAccounts = async (): Promise<BillingAccountData[]> => {
  const path = '/api/super-admin/billing-accounts'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get billing accounts. ${reason}`)
  }
  return response.json()
}

export const getBillingAccountById = async (id: number): Promise<BillingAccountData> => {
  const path = `/api/super-admin/billing-accounts/${id}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get billing account. ${reason}`)
  }
  return response.json()
}

export const createBillingAccount = async (
  data: CreateBillingAccountRequest
): Promise<BillingAccountData> => {
  const path = '/api/super-admin/billing-accounts'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to create billing account. ${reason}`)
  }
  return response.json()
}

export const updateBillingAccount = async (
  id: number,
  data: UpdateBillingAccountRequest
): Promise<BillingAccountData> => {
  const path = `/api/super-admin/billing-accounts/${id}`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ ...data, id })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update billing account. ${reason}`)
  }
  return response.json()
}

export const deleteBillingAccount = async (id: number): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${id}`
  const response = await fetchWrapper(path, {
    method: 'DELETE'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to delete billing account. ${reason}`)
  }
}

export const getAccountTypes = async (): Promise<AccountTypeOption[]> => {
  const path = '/api/super-admin/billing-accounts/account-types/list'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get account types. ${reason}`)
  }
  return response.json()
}

export const getBillingMembers = async (billingId: number): Promise<BillingMember[]> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/members`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get billing members. ${reason}`)
  }
  return response.json()
}

export const addBillingMember = async (
  billingId: number,
  email: string,
  role = 'admin'
): Promise<BillingMember> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/members`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, role })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to add billing member. ${reason}`)
  }
  return response.json()
}

export const removeBillingMember = async (billingId: number, userId: number): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/members/${userId}`
  const response = await fetchWrapper(path, {
    method: 'DELETE'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to remove billing member. ${reason}`)
  }
}

export const cancelBillingInvitation = async (
  billingId: number,
  invitationId: number
): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/invitations/${invitationId}`
  const response = await fetchWrapper(path, {
    method: 'DELETE'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to cancel invitation. ${reason}`)
  }
}

export const resendBillingInvitation = async (
  billingId: number,
  invitationId: number
): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/invitations/${invitationId}/resend`
  const response = await fetchWrapper(path, {
    method: 'POST'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to resend invitation. ${reason}`)
  }
}

export interface BulkInviteRequest {
  emails: string[]
  workspace_ids?: number[]
  as_account_admin?: boolean
}

export interface BulkInviteResponse {
  success: boolean
  message: string
  invited: string[]
  failed: string[]
}

export const bulkInviteBillingMembers = async (
  billingId: number,
  request: BulkInviteRequest
): Promise<BulkInviteResponse> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/invite-members`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to send invitations'))
  }
  return response.json()
}

export interface BillingWorkspace {
  org_id: number
  org_name: string
}

export interface BillingWorkspaceDetails {
  org_id: number
  org_name: string
  uuid: string
  org_type: string
  billing_id: number | null
  created_time: string | null
  updated_time: string | null
  member_count: number
  is_default: boolean // True for Personal Workspace - cannot be deleted or shared
}

export const getBillingWorkspaces = async (billingId: number): Promise<BillingWorkspace[]> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get billing workspaces. ${reason}`)
  }
  return response.json()
}

export const getBillingWorkspacesDetails = async (
  billingId: number
): Promise<BillingWorkspaceDetails[]> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces/details`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get billing workspaces details. ${reason}`)
  }
  return response.json()
}

export interface CreateWorkspaceRequest {
  org_name: string
}

export interface UpdateWorkspaceRequest {
  org_name?: string
  billing_id?: number
  is_default?: boolean
}

export const createBillingWorkspace = async (
  billingId: number,
  data: CreateWorkspaceRequest
): Promise<BillingWorkspaceDetails> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to create workspace. ${reason}`)
  }
  return response.json()
}

export const updateBillingWorkspace = async (
  billingId: number,
  orgId: number,
  data: UpdateWorkspaceRequest
): Promise<BillingWorkspaceDetails> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces/${orgId}`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update workspace. ${reason}`)
  }
  return response.json()
}

export const deleteBillingWorkspace = async (billingId: number, orgId: number): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces/${orgId}`
  const response = await fetchWrapper(path, {
    method: 'DELETE'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to delete workspace. ${reason}`)
  }
}

// ============================================================================
// Workspace Members
// ============================================================================

export interface WorkspaceMember {
  user_id: number
  email: string
  name: string | null
  role: 'admin' | 'user' | 'account_admin'
  is_account_admin: boolean
  email_verified: boolean | null
  phone_verified: boolean | null
  created_time: string | null
}

export const getWorkspaceMembers = async (
  billingId: number,
  orgId: number
): Promise<WorkspaceMember[]> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces/${orgId}/members`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get workspace members. ${reason}`)
  }
  return response.json()
}

export const removeWorkspaceMember = async (
  billingId: number,
  orgId: number,
  userId: number
): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/workspaces/${orgId}/members/${userId}`
  const response = await fetchWrapper(path, {
    method: 'DELETE'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to remove workspace member. ${reason}`)
  }
}

// ============================================================================
// User Verification Management
// ============================================================================

export interface UserListItem {
  user_id: number
  email: string
  name: string | null
  created_time: string | null
  email_verified: boolean
  phone_verified: boolean
  phone_number: string | null
  mfa_enabled: boolean
  unsuccessful_login_attempts: number
  is_account_locked: boolean
}

export interface UserVerificationStatus {
  user_id: number
  email: string
  name: string | null
  email_verified: boolean | null
  phone_number: string | null
  phone_verified: boolean | null
  mfa_enabled: boolean | null
  created_time: string | null
}

export const getAllUsers = async (): Promise<UserListItem[]> => {
  const path = '/api/super-admin/users'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get users list. ${reason}`)
  }
  return response.json()
}

export const getUserVerificationStatus = async (
  userId: number
): Promise<UserVerificationStatus> => {
  const path = `/api/super-admin/users/${userId}/verification-status`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user verification status. ${reason}`)
  }
  return response.json()
}

export const getUserVerificationStatusByEmail = async (
  email: string
): Promise<UserVerificationStatus> => {
  const path = `/api/super-admin/users/by-email/${encodeURIComponent(email)}/verification-status`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user verification status. ${reason}`)
  }
  return response.json()
}

export const setUserEmailVerified = async (
  userId: number,
  emailVerified: boolean
): Promise<void> => {
  const path = `/api/super-admin/users/${userId}/email-verified`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email_verified: emailVerified })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set email verification. ${reason}`)
  }
}

export const setUserPhoneVerified = async (
  userId: number,
  phoneVerified: boolean
): Promise<void> => {
  const path = `/api/super-admin/users/${userId}/phone-verified`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ phone_verified: phoneVerified })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set phone verification. ${reason}`)
  }
}

export const setUserMfaEnabled = async (userId: number, mfaEnabled: boolean): Promise<void> => {
  const path = `/api/super-admin/users/${userId}/mfa-enabled`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ mfa_enabled: mfaEnabled })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set MFA status. ${reason}`)
  }
}

export interface UnlockAccountResponse {
  success: boolean
  message: string
  user_id: number
  email: string
  previous_attempts: number
}

export const unlockUserAccountByAdmin = async (userId: number): Promise<UnlockAccountResponse> => {
  const path = `/api/super-admin/users/${userId}/unlock-account`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to unlock user account. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Billing Invitation (Public) Operations
// ============================================================================

export interface InvitationInfo {
  valid: boolean
  email?: string
  account_name?: string
  inviter_email?: string
  user_exists: boolean
  error?: string
}

export const getBillingInvitationInfo = async (token: string): Promise<InvitationInfo> => {
  const path = `/api/billing-invite/info?token=${encodeURIComponent(token)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get invitation info. ${reason}`)
  }
  return response.json()
}

export const acceptBillingInvitation = async (
  token: string
): Promise<{ success: boolean; message: string; billing_id?: number; account_name?: string }> => {
  const path = '/api/billing-invite/accept'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ token })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to accept invitation. ${reason}`)
  }
  return response.json()
}

export const acceptBillingInvitationAfterSignup = async (
  token: string
): Promise<{ success: boolean; message: string; billing_id?: number; account_name?: string }> => {
  const path = '/api/billing-invite/accept-after-signup'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ token })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to accept invitation. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// License Management Operations
// ============================================================================

export interface LicenseType {
  id: number
  name: string
  label: string
  description: string | null
  quota: number | null
  billable_flows_limit: number | null
  duration: number | null
  duration_unit: string | null
  billing_model: 'prepaid' | 'subscription' | 'per_seat' | 'custom' // prepaid=Flex pay-as-you-go, subscription/per_seat=Stripe, custom=enterprise
}

export interface LicenseInstance {
  id: number
  license_type_id: number
  license_type_name: string
  license_type_label: string
  billing_id: number
  start_date: string
  end_date: string
  status: string // Computed from dates: "active" (within date range), "pending" (before start), "inactive" (expired)
  billing_type: 'quota' | 'billable_flow' // Billing model: quota (credits) or billable_flow (per-migration)
  quota: number | null // Credit limit (for quota-based plans)
  billable_flows_quota: number | null // Flow limit (for billable-flow plans)
  subscription_id: string | null
  is_billing_period: boolean // Whether this license's dates define the billing period
  auto_renew: boolean // Whether the license auto-renews (synced with Stripe)
  contract_end_date: string | null // Hard stop date - when license truly ends (synced to Stripe cancel_at)
  billing_package?: string | null // Stripe cadence: monthly or six_month
  credit_period_start?: string | null // Current included-credit window start
  credit_period_end?: string | null // Current included-credit window end
  // Credit fields from instance_metadata
  carried_over_credits: number | null // Credits rolled over from previous plan
  purchased_credits: number | null // Credits purchased via prepay
  // Enterprise contract fields (only populated for custom/enterprise billing)
  billing_model: string | null // prepaid, subscription, per_seat, custom
  contract_value_cents: number | null
  billing_schedule: string | null // monthly, quarterly, annually, one_time
  payment_terms_days: number | null
  stripe_customer_id: string | null
  invoices_created: string[] | null
  total_invoiced_cents: number | null
  total_paid_cents: number | null
  // Manual recurring fields
  manual_recurring: boolean
  renewal_interval_months: number | null // 1, 3, 6, or 12
  recurring_end_date: string | null // Hard stop for manual recurring renewals
  // Full metadata for detailed view
  instance_metadata: Record<string, unknown> | null
  // Soft-delete state — backend returns deleted=true for soft-deleted rows. Used
  // by the super-admin UI to hide them by default behind a "Show deleted" toggle.
  deleted?: boolean
  deleted_at?: string | null
}

export interface AddLicenseRequest {
  license_type_id: number
  start_date: string // ISO format date string
  end_date: string // ISO format date string
  quota?: number
  billable_flows_quota?: number
  subscription_id?: string // Stripe subscription ID - if provided, syncs from Stripe
  // Manual recurring (no Stripe) fields
  manual_recurring?: boolean
  renewal_interval_months?: number // 1, 3, 6, or 12
  recurring_end_date?: string // Hard stop for manual recurring renewals (required when manual_recurring is true)
  // Enterprise contract fields (only used when billing_model is "custom")
  contract_value_cents?: number
  billing_schedule?: string // monthly, quarterly, annually, one_time
  payment_terms_days?: number // Net 30, Net 60, etc.
}

export interface UpdateLicenseRequest {
  license_type_id?: number
  start_date?: string // ISO format date string
  end_date?: string // ISO format date string
  quota?: number // Use -1 to set quota to unlimited (null)
  subscription_id?: string
  billable_flows_quota?: number // Use -1 to set quota to unlimited (null)
  // "monthly"/"six_month" are Stripe pricing tiers; "none" is the manual-license
  // opt-out (credit period equals the access window — no monthly slicing).
  billing_package?: 'monthly' | 'six_month' | 'none'
  auto_renew?: boolean // Toggle auto-renew (synced with Stripe)
  contract_end_date?: string // Hard stop date - when license truly ends (synced to Stripe cancel_at). Empty string to clear.
  // Manual recurring (no Stripe) fields
  manual_recurring?: boolean
  renewal_interval_months?: number // 1, 3, 6, or 12
  recurring_end_date?: string // Hard stop for manual recurring renewals. Empty string to clear.
  // Enterprise contract fields (only used when billing_model is "custom")
  contract_value_cents?: number | null // Total contract value in cents (null to clear)
  billing_schedule?: string | null // monthly, quarterly, annually, one_time (null to clear)
  payment_terms_days?: number | null // Net 30, Net 60, etc. (null to clear)
}

const extractErrorMessage = async (response: Response, defaultMessage: string): Promise<string> => {
  try {
    const errorJson = JSON.parse(await response.text())
    return extractApiErrorMessage(errorJson, defaultMessage)
  } catch {
    return defaultMessage
  }
}

export const getLicenseTypes = async (): Promise<LicenseType[]> => {
  const path = '/api/super-admin/license-types'
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to get license types')
    throw new Error(errorMessage)
  }
  return response.json()
}

export const getBillingLicenses = async (billingId: number): Promise<LicenseInstance[]> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to get licenses')
    throw new Error(errorMessage)
  }
  return response.json()
}

export const addBillingLicense = async (
  billingId: number,
  data: AddLicenseRequest
): Promise<LicenseInstance> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to add license')
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Delete a billing license.
 * @param billingId - The billing account ID
 * @param licenseId - The license ID to delete
 * @param permanent - If true, permanently removes from DB. If false, just sets status to inactive.
 */
export const deleteBillingLicense = async (
  billingId: number,
  licenseId: number,
  permanent: boolean = false
): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}?permanent=${permanent}`
  const response = await fetchWrapper(path, {
    method: 'DELETE',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(
      response,
      permanent ? 'Failed to permanently delete license' : 'Failed to delete license'
    )
    throw new Error(errorMessage)
  }
}

/**
 * Revoke a billing license by setting end_date to now (makes it inactive).
 * Unlike delete, the license remains visible in the list as "inactive".
 * @param billingId - The billing account ID
 * @param licenseId - The license ID to revoke
 */
export const revokeBillingLicense = async (billingId: number, licenseId: number): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}/revoke`
  const response = await fetchWrapper(path, {
    method: 'PATCH',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to revoke license')
    throw new Error(errorMessage)
  }
}

/** Restore a soft-deleted license. Clears deleted/archived flags; dates are
 *  preserved as-is, so the row's computed status depends on its existing
 *  start_date/end_date. */
export const restoreBillingLicense = async (
  billingId: number,
  licenseId: number
): Promise<void> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}/restore`
  const response = await fetchWrapper(path, {
    method: 'PATCH',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to restore license')
    throw new Error(errorMessage)
  }
}

export const updateBillingLicense = async (
  billingId: number,
  licenseId: number,
  data: UpdateLicenseRequest
): Promise<LicenseInstance> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}`
  const response = await fetchWrapper(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to update license')
    throw new Error(errorMessage)
  }
  return response.json()
}

// ============================================================================
// Stripe Subscription Preview and Unlink
// ============================================================================

export interface StripeSubscriptionPreview {
  subscription_id: string
  status?: string // active, past_due, canceled, etc.
  start_date?: string
  end_date?: string
  license_type_id?: number
  license_type_name?: string
  license_type_label?: string
  cancel_at_period_end: boolean
  cancel_at?: string
  customer_id?: string
  customer_email?: string
  product_name?: string
  price_amount?: number // unit price in cents
  price_currency?: string
  price_interval?: string // month, year
  quantity?: number // number of seats/units
  total_amount?: number // total price in cents (unit_price * quantity)
  metadata: Record<string, string>
  error?: string
}

/**
 * Preview Stripe subscription data before applying it to a license.
 * Returns subscription details including detected plan tier, dates, and customer info.
 */
export const previewStripeSubscription = async (
  subscriptionId: string
): Promise<StripeSubscriptionPreview> => {
  const path = `/api/super-admin/stripe/subscription/${encodeURIComponent(subscriptionId)}/preview`
  const response = await fetchWrapper(path, {
    method: 'GET',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(
      response,
      'Failed to preview Stripe subscription'
    )
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Unlink a license from its Stripe subscription.
 * Clears subscription_id and Stripe metadata, allowing manual management.
 * Note: Does NOT cancel the Stripe subscription - only disconnects Curie tracking.
 */
export const unlinkLicenseFromStripe = async (
  billingId: number,
  licenseId: number
): Promise<LicenseInstance> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}/unlink-stripe`
  const response = await fetchWrapper(path, {
    method: 'POST',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to unlink license from Stripe')
    throw new Error(errorMessage)
  }
  return response.json()
}

// ============================================================================
// License with Stripe Payment (Admin-Initiated Subscription)
// ============================================================================

export interface CreateLicenseWithPaymentRequest {
  license_type_id: number
  quota?: number // Override license type default
  billable_flows_quota?: number
  billing_package?: 'monthly' | 'six_month' // Stripe package to use for subscription/per-seat plans
  contract_end_date?: string // Hard stop date (ISO format)
  success_url: string // Where to redirect after successful payment
  cancel_url: string // Where to redirect if payment is canceled
}

export interface CreateLicenseWithPaymentResponse {
  payment_url: string // Stripe checkout URL
  pending_license_id: number // ID of the pending license
  checkout_session_id: string // Stripe checkout session ID
  expires_at: string // When the payment link expires (ISO format)
}

/**
 * Create a license with Stripe payment link.
 * Admin configures the license, user pays via link, license auto-activates.
 */
export const createLicenseWithPayment = async (
  billingId: number,
  data: CreateLicenseWithPaymentRequest
): Promise<CreateLicenseWithPaymentResponse> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/with-payment`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(
      response,
      'Failed to create license with payment'
    )
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Cancel a pending license that was created with payment link.
 */
export const cancelPendingLicense = async (
  billingId: number,
  licenseId: number
): Promise<{ message: string; license_id: number; checkout_session_expired: boolean }> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}/cancel-pending`
  const response = await fetchWrapper(path, {
    method: 'DELETE',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to cancel pending license')
    throw new Error(errorMessage)
  }
  return response.json()
}

// ============================================================================
// Enterprise Invoice Management (Custom Billing)
// ============================================================================

export interface CreateEnterpriseInvoiceRequest {
  license_id?: number // License to track invoice against (optional)
  description: string // Invoice item description
  amount_cents: number // Amount in cents (e.g., 10000 = $100.00)
  currency?: string // Default: usd
  send_invoice?: boolean // Whether to email the invoice (default: true)
}

export interface EnterpriseInvoiceResponse {
  invoice_id: string
  invoice_number: string | null
  amount_due: number // Amount in cents
  currency: string
  status: string
  hosted_invoice_url: string | null // URL for customer to pay
  pdf_url: string | null // PDF download URL
  created_at: string
}

export interface BillingInvoice {
  id: string
  number: string | null
  amount_due: number
  amount_paid: number
  currency: string
  status: string
  hosted_invoice_url: string | null
  pdf_url: string | null
  created_at: string | null
  due_date: string | null
  paid_at: string | null
  description: string | null
  invoice_type: string // 'subscription' or 'enterprise_manual'
}

/**
 * Create a manual invoice for enterprise customers.
 */
export const createEnterpriseInvoice = async (
  billingId: number,
  data: CreateEnterpriseInvoiceRequest
): Promise<EnterpriseInvoiceResponse> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/enterprise-invoices`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to create invoice')
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Get all invoices for a billing account.
 */
export const getBillingInvoices = async (billingId: number): Promise<BillingInvoice[]> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/invoices`
  const response = await fetchWrapper(path, {
    method: 'GET',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to fetch invoices')
    throw new Error(errorMessage)
  }
  return response.json()
}

// ============================================================================
// License-Stripe Sync Verification and Management
// ============================================================================

export interface LicenseStripeSyncStatus {
  license_id: number
  subscription_id: string | null
  is_synced: boolean
  has_stripe_subscription: boolean
  discrepancies: Array<{
    field: string
    curie: string | boolean | null
    stripe: string | boolean | null
    note?: string
  }>
  curie_values: {
    start_date: string | null
    end_date: string | null
    status: string | null
    cancel_at_period_end?: boolean
  }
  stripe_values: {
    current_period_start: string | null
    current_period_end: string | null
    cancel_at: string | null
    status: string | null
    cancel_at_period_end: boolean
  } | null
  last_checked: string
}

/**
 * Check if a license is in sync with its Stripe subscription.
 * Returns detailed comparison between Curie and Stripe data.
 */
export const checkLicenseStripeSync = async (
  billingId: number,
  licenseId: number
): Promise<LicenseStripeSyncStatus> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}/stripe-sync`
  const response = await fetchWrapper(path, { suppressErrorToast: true })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to check license sync status')
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Force sync a license from its Stripe subscription.
 * Pulls latest data from Stripe and updates the Curie license.
 */
export const syncLicenseFromStripe = async (
  billingId: number,
  licenseId: number
): Promise<LicenseInstance> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/licenses/${licenseId}/sync-from-stripe`
  const response = await fetchWrapper(path, {
    method: 'POST',
    suppressErrorToast: true
  })
  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to sync license from Stripe')
    throw new Error(errorMessage)
  }
  return response.json()
}

// ============================================================================
// Blocked Email Domains CRUD
// ============================================================================

export interface BlockedDomainsResponse {
  domains: string[]
  count: number
  message?: string
}

export const getBlockedEmailDomains = async (): Promise<BlockedDomainsResponse> => {
  const path = '/api/super-admin/settings/blocked-email-domains'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get blocked email domains. ${reason}`)
  }
  return response.json()
}

export const setBlockedEmailDomains = async (
  domains: string[]
): Promise<BlockedDomainsResponse> => {
  const path = '/api/super-admin/settings/blocked-email-domains'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ domains })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set blocked email domains. ${reason}`)
  }
  return response.json()
}

export const addBlockedEmailDomain = async (domain: string): Promise<BlockedDomainsResponse> => {
  const path = '/api/super-admin/settings/blocked-email-domains/add'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ domain })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to add blocked email domain. ${reason}`)
  }
  return response.json()
}

export const removeBlockedEmailDomain = async (domain: string): Promise<BlockedDomainsResponse> => {
  const path = '/api/super-admin/settings/blocked-email-domains/remove'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ domain })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to remove blocked email domain. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Allowed Email Domains (Allowlist - skip API validation)
// ============================================================================

export interface AllowedDomainsResponse {
  domains: string[]
  count: number
  message?: string
}

export const getAllowedEmailDomains = async (): Promise<AllowedDomainsResponse> => {
  const path = '/api/super-admin/settings/allowed-email-domains'
  const response = await fetchWrapper(path, {
    method: 'GET'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get allowed email domains. ${reason}`)
  }
  return response.json()
}

export const setAllowedEmailDomains = async (
  domains: string[]
): Promise<AllowedDomainsResponse> => {
  const path = '/api/super-admin/settings/allowed-email-domains'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ domains })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set allowed email domains. ${reason}`)
  }
  return response.json()
}

export const addAllowedEmailDomain = async (domain: string): Promise<AllowedDomainsResponse> => {
  const path = '/api/super-admin/settings/allowed-email-domains/add'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ domain })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to add allowed email domain. ${reason}`)
  }
  return response.json()
}

export const removeAllowedEmailDomain = async (domain: string): Promise<AllowedDomainsResponse> => {
  const path = '/api/super-admin/settings/allowed-email-domains/remove'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ domain })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to remove allowed email domain. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Whitelisted Emails (individual emails that bypass domain blocks)
// ============================================================================

export interface WhitelistedEmailsResponse {
  emails: string[]
  count: number
  message?: string
}

export const getWhitelistedEmails = async (): Promise<WhitelistedEmailsResponse> => {
  const path = '/api/super-admin/settings/whitelisted-emails'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get whitelisted emails. ${reason}`)
  }
  return response.json()
}

export const setWhitelistedEmails = async (
  emails: string[]
): Promise<WhitelistedEmailsResponse> => {
  const path = '/api/super-admin/settings/whitelisted-emails'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ emails })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set whitelisted emails. ${reason}`)
  }
  return response.json()
}

export const addWhitelistedEmail = async (email: string): Promise<WhitelistedEmailsResponse> => {
  const path = '/api/super-admin/settings/whitelisted-emails/add'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to add whitelisted email. ${reason}`)
  }
  return response.json()
}

export const removeWhitelistedEmail = async (email: string): Promise<WhitelistedEmailsResponse> => {
  const path = '/api/super-admin/settings/whitelisted-emails/remove'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to remove whitelisted email. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Impersonation (Read-Only User Viewing for Debugging)
// ============================================================================

export interface ImpersonationStatus {
  is_impersonating: boolean
  impersonated_user_id: number | null
  impersonated_user_email: string | null
  impersonated_user_name: string | null
  admin_user_id: number | null
  admin_user_email: string | null
  return_url: string | null
}

export interface ImpersonatedUserWorkspace {
  org_id: number
  org_name: string
  workspace_uuid: string
  role: string
}

export interface ImpersonationInfo {
  status: ImpersonationStatus
  workspaces: ImpersonatedUserWorkspace[]
}

export interface SearchUserResult {
  user_id: number
  email: string
  name: string | null
}

/**
 * Start impersonating a user (super admin only).
 * This enables read-only viewing of the application as the specified user.
 *
 * @param params.user_id - User ID to impersonate
 * @param params.email - Email of user to impersonate (alternative to user_id)
 * @param params.return_url - URL to return to after impersonation ends (e.g., current page)
 */
export const startImpersonation = async (params: {
  user_id?: number
  email?: string
  return_url?: string
}): Promise<ImpersonationInfo> => {
  const path = '/api/super-admin/impersonation/start'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(params)
  })
  if (!response.ok) {
    const text = await response.text()
    let errorMessage = 'Failed to start impersonation'
    try {
      const json = JSON.parse(text)
      if (json.detail) {
        errorMessage = json.detail
      }
    } catch {
      if (text) {
        errorMessage = text
      }
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Stop impersonating a user and return to normal mode.
 */
export const stopImpersonation = async (): Promise<ImpersonationStatus> => {
  const path = '/api/super-admin/impersonation/stop'
  const response = await fetchWrapper(path, {
    method: 'POST'
  })
  if (!response.ok) {
    const text = await response.text()
    let errorMessage = 'Failed to stop impersonation'
    try {
      const json = JSON.parse(text)
      if (json.detail) {
        errorMessage = json.detail
      }
    } catch {
      if (text) {
        errorMessage = text
      }
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

/**
 * Get current impersonation status.
 */
export const getImpersonationStatus = async (): Promise<ImpersonationInfo> => {
  const path = '/api/super-admin/impersonation/status'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get impersonation status. ${reason}`)
  }
  return response.json()
}

/**
 * Search for users to impersonate.
 */
export const searchUsersForImpersonation = async (query: string): Promise<SearchUserResult[]> => {
  const path = `/api/super-admin/impersonation/search-users?q=${encodeURIComponent(query)}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to search users. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// User Details API (Comprehensive User Information)
// ============================================================================

export interface UserProfileInfo {
  user_id: number
  email: string
  name: string
  username: string
  avatar: string | null
  created_time: string | null
  updated_time: string | null
}

export interface UserVerificationInfo {
  email_verified: boolean
  phone_number: string | null
  phone_verified: boolean
  mfa_enabled: boolean
  active_sessions_count: number
  unsuccessful_login_attempts: number
}

export interface UserBillingAccountInfo {
  billing_id: number
  account_name: string
  role: string
  is_account_admin: boolean
}

export interface UserWorkspaceInfo {
  org_id: number
  org_name: string
  workspace_uuid: string
  role: string
  billing_id: number | null
  is_personal: boolean
}

export interface UserQuotaInfo {
  billing_id: number
  account_name: string
  license_name: string
  credits_used: number
  credits_limit: number
  credits_remaining: number
  utilization_percent: number
  period_start: string | null
  period_end: string | null
  is_free_license: boolean
}

export interface UserActivityStats {
  total_tasks: number
  tasks_last_30_days: number
  last_task_date: string | null
  credits_used_last_30_days: number
}

export interface UserDetailsResponse {
  profile: UserProfileInfo
  verification: UserVerificationInfo
  billing_accounts: UserBillingAccountInfo[]
  workspaces: UserWorkspaceInfo[]
  quota: UserQuotaInfo | null
  activity: UserActivityStats
}

export interface UserBillingInfoResponse {
  user_id: number
  billing_accounts: UserQuotaInfo[]
}

export interface UserTaskInfo {
  task_id: number
  cmd_type: string
  status: string
  created_time: string | null
  conversation_id: number
}

export interface UserTasksResponse {
  user_id: number
  total_count: number
  tasks: UserTaskInfo[]
}

/**
 * Get comprehensive details for a specific user.
 */
export const getUserDetails = async (userId: number): Promise<UserDetailsResponse> => {
  const path = `/api/super-admin/users/${userId}/details`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user details. ${reason}`)
  }
  return response.json()
}

/**
 * Access log entry for a user.
 */
export interface UserAccessLog {
  timestamp: string
  action: string
  ip_address: string
  user_agent: string
  details: string
}

/**
 * Get access logs for a user by email.
 * Returns parsed array of access log entries.
 */
export const getUserAccessLogs = async (
  email: string,
  startDate: string,
  endDate: string
): Promise<UserAccessLog[]> => {
  const params = new URLSearchParams()
  params.append('email', email)
  params.append('startDate', startDate)
  params.append('endDate', endDate)

  const url = `/api/super-admin/lookup/email-access-logs?${params.toString()}`
  const response = await fetchWrapper(url, {
    headers: {
      Accept: 'text/csv'
    }
  })

  if (!response.ok) {
    const text = await response.text()
    let errorMessage = 'Failed to get access logs'
    try {
      const json = JSON.parse(text)
      if (json.detail) {
        errorMessage = json.detail
      }
    } catch {
      if (text) {
        errorMessage = text
      }
    }
    throw new Error(errorMessage)
  }

  // Parse CSV to array of objects
  const text = await response.text()
  const lines = text.split('\n').filter(line => line.trim())
  if (lines.length < 2) {
    return []
  }

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'))
  const logs: UserAccessLog[] = []

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',')
    const entry: Record<string, string> = {}
    headers.forEach((header, idx) => {
      entry[header] = values[idx]?.trim() || ''
    })
    logs.push({
      // Map various possible header names from the CSV
      timestamp: entry.time_stamp || entry.timestamp || entry.created_time || entry.time || '',
      action: entry.action_type || entry.action || entry.event || entry.type || '',
      ip_address: entry.client_ip || entry.ip_address || entry.ip || '',
      user_agent: entry.user_agent || entry.agent || '',
      details: entry.details || entry.description || ''
    })
  }

  return logs
}

/**
 * Get billing and quota information for a user.
 */
export const getUserBillingInfo = async (userId: number): Promise<UserBillingInfoResponse> => {
  const path = `/api/super-admin/users/${userId}/billing-info`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user billing info. ${reason}`)
  }
  return response.json()
}

/**
 * Revoke all active sessions for a user (force logout).
 */
export const revokeUserSessions = async (
  userId: number
): Promise<{ success: boolean; message: string; sessions_revoked: number }> => {
  const path = `/api/super-admin/users/${userId}/revoke-sessions`
  const response = await fetchWrapper(path, {
    method: 'POST'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to revoke sessions. ${reason}`)
  }
  return response.json()
}

/**
 * Resend email verification to a user.
 */
export const resendVerificationEmail = async (
  userId: number
): Promise<{ success: boolean; message: string }> => {
  const path = `/api/super-admin/users/${userId}/resend-verification-email`
  const response = await fetchWrapper(path, {
    method: 'POST'
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to resend verification email. ${reason}`)
  }
  return response.json()
}

/**
 * Get recent tasks for a user.
 */
export const getUserTasks = async (userId: number, limit = 50): Promise<UserTasksResponse> => {
  const path = `/api/super-admin/users/${userId}/tasks?limit=${limit}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get user tasks. ${reason}`)
  }
  return response.json()
}

// =============================================================================
// Script Runner Types and Functions
// =============================================================================

export interface ScriptInfo {
  name: string
  description: string
  param_schema: Record<string, unknown> | null
}

export interface ScriptAction {
  action_type: string
  target: string
  description: string
  details: Record<string, unknown>
}

export interface ScriptExecuteResponse {
  status: 'success' | 'partial' | 'failed' | 'dry_run'
  message: string
  actions: ScriptAction[]
  summary: Record<string, number>
  errors: string[]
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
}

export interface ScriptExecuteRequest {
  params: Record<string, unknown>
  dry_run: boolean
}

/**
 * List all available scripts
 */
export const listScripts = async (): Promise<{ scripts: ScriptInfo[] }> => {
  const path = '/api/super-admin/scripts'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to list scripts. ${reason}`)
  }
  return response.json()
}

/**
 * Get information about a specific script
 */
export const getScriptInfo = async (scriptName: string): Promise<ScriptInfo> => {
  const path = `/api/super-admin/scripts/${scriptName}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get script info. ${reason}`)
  }
  return response.json()
}

/**
 * Execute a script
 */
export const executeScript = async (
  scriptName: string,
  request: ScriptExecuteRequest
): Promise<ScriptExecuteResponse> => {
  const path = `/api/super-admin/scripts/${scriptName}/execute`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to execute script. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// Runtime Config (DB-based, instant effect)
// ============================================================================

export interface RuntimeConfigItem {
  key: string
  value: unknown
  type: string
  is_secret: boolean
}

export interface SeedResult {
  message: string
  seeded: string[]
  skipped_secret: string[]
  skipped_existing: string[]
}

export const getRuntimeConfig = async (): Promise<{
  configs: RuntimeConfigItem[]
  count: number
}> => {
  const path = '/api/super-admin/runtime-config'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get runtime config. ${reason}`)
  }
  return response.json()
}

export const setRuntimeConfig = async (
  key: string,
  value: unknown
): Promise<{ message: string; key: string; value: unknown }> => {
  const path = '/api/super-admin/runtime-config'
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set runtime config. ${reason}`)
  }
  return response.json()
}

export const deleteRuntimeConfig = async (
  key: string
): Promise<{ message: string; key: string }> => {
  const path = '/api/super-admin/runtime-config'
  const response = await fetchWrapper(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to delete runtime config. ${reason}`)
  }
  return response.json()
}

export const seedRuntimeConfig = async (): Promise<SeedResult> => {
  const path = '/api/super-admin/runtime-config/seed'
  const response = await fetchWrapper(path, { method: 'POST' })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to seed runtime config. ${reason}`)
  }
  return response.json()
}

// ============================================================================
// App Config Secret (current environment's api/config in Secrets Manager)
// ============================================================================

export interface AppConfigSecret {
  name: string
  is_json: boolean
  data: Record<string, unknown> | string
  key_count: number | null
}

export const getAppConfigSecret = async (): Promise<AppConfigSecret> => {
  const path = '/api/super-admin/secrets/config'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get app config. ${reason}`)
  }
  return response.json()
}

// ── Usage Events (CUR-173) ──────────────────────────────────────────

export type UsageEventSource = 'mcp' | 'internal' | 'user' | 'system'

export interface UsageEventItem {
  id: number
  created_at_unix: number
  org_id: number
  org_name: string | null
  user_id: number | null
  user_email: string | null
  conversation_id: number | null
  task_id: number | null
  metric_name: string
  metric_value: number
  source: UsageEventSource
  api_token_id: number | null
  api_token_truncated: string | null
  tool_name: string | null
  status: string | null
  latency_ms: number | null
  client_name: string | null
  client_user_agent: string | null
  is_internal: boolean
  metadata: Record<string, unknown>
}

export interface UsageEventsResponse {
  items: UsageEventItem[]
  next_cursor: string | null
}

export interface UsageEventsFacets {
  days: number
  by_metric: { metric_name: string; count: number }[]
  by_source: Record<UsageEventSource, number>
}

export interface ListUsageEventsParams {
  metricNames?: string[]
  source?: UsageEventSource
  orgId?: number
  search?: string
  sinceUnix?: number
  untilUnix?: number
  cursor?: string
  limit?: number
}

export const getSuperAdminUsageEvents = async (
  params: ListUsageEventsParams = {}
): Promise<UsageEventsResponse> => {
  const search = new URLSearchParams()
  for (const m of params.metricNames ?? []) search.append('metric_name', m)
  if (params.source) search.set('source', params.source)
  if (params.orgId !== undefined) search.set('org_id', String(params.orgId))
  if (params.search) search.set('search', params.search)
  if (params.sinceUnix !== undefined) search.set('since_unix', String(params.sinceUnix))
  if (params.untilUnix !== undefined) search.set('until_unix', String(params.untilUnix))
  if (params.cursor) search.set('cursor', params.cursor)
  if (params.limit !== undefined) search.set('limit', String(params.limit))
  const qs = search.toString()
  const path = qs ? `/api/super-admin/usage-events?${qs}` : '/api/super-admin/usage-events'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await extractApiErrorMessage(response)
    throw new Error(`Failed to load usage events. ${reason}`)
  }
  return response.json()
}

export const getSuperAdminUsageEventsFacets = async (
  days: number = 30
): Promise<UsageEventsFacets> => {
  const path = `/api/super-admin/usage-events/facets?days=${days}`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await extractApiErrorMessage(response)
    throw new Error(`Failed to load usage events facets. ${reason}`)
  }
  return response.json()
}

export interface TaskCmdTypeCount {
  cmd_type: string
  count: number
}

export interface S3PrefixFootprint {
  prefix: string
  object_count: number
  total_bytes: number
  inventory_pending: boolean
  snapshot_at?: string | null
}

export interface DataFootprintResponse {
  billing_id: number
  billing_name: string | null
  org_count: number
  org_ids: number[]
  member_count: number
  user_count: number
  conversation_count: number
  message_count: number
  task_count: number
  tasks_by_cmd_type: TaskCmdTypeCount[]
  usage_event_count: number
  usage_events_trailing_365d: number
  earliest_activity: string | null
  latest_activity: string | null
  s3_bucket: string | null
  s3_prefixes: S3PrefixFootprint[]
  s3_total_object_count: number
  s3_total_bytes: number
  s3_has_pending_inventory: boolean
}

export const getDataFootprint = async (billingId: number): Promise<DataFootprintResponse> => {
  const path = `/api/super-admin/billing-accounts/${billingId}/data-footprint`
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await extractApiErrorMessage(response)
    throw new Error(`Failed to load data footprint. ${reason}`)
  }
  return response.json()
}

export interface S3ScanProgress {
  started_at: string
  last_updated: string
  current_prefix: string | null
  total_scanned: number
  error?: string | null
}

export interface S3ScanStatus {
  bucket: string | null
  scanned_at: string | null
  scan_duration_seconds: number | null
  total_objects_scanned: number | null
  org_count_in_snapshot: number | null
  prefixes_scanned: string[]
  in_progress: boolean
  progress: S3ScanProgress | null
}

export interface RunS3ScanResponse {
  started: boolean
  message: string | null
}

export const getS3ScanStatus = async (): Promise<S3ScanStatus> => {
  const path = '/api/super-admin/s3-footprint-scan/status'
  const response = await fetchWrapper(path)
  if (!response.ok) {
    const reason = await extractApiErrorMessage(response)
    throw new Error(`Failed to load S3 scan status. ${reason}`)
  }
  return response.json()
}

export const runS3FootprintScan = async (): Promise<RunS3ScanResponse> => {
  const path = '/api/super-admin/s3-footprint-scan'
  const response = await fetchWrapper(path, { method: 'POST' })
  if (!response.ok) {
    const reason = await extractApiErrorMessage(response)
    throw new Error(`Failed to run S3 scan. ${reason}`)
  }
  return response.json()
}
